import { useCallback, useState } from 'react';

import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';

import getMemoByClientAndAllocation from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/model/getMemoByClientAndAllocation';
import { IEditAllocationFormValues } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/model/types';
import { useEditClientAllocationContext } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/EditClientAllocation/context/EditClientAllocationContext';

const useEditCrisisAllocationSubmit = () => {
  const { clinicId, allocations } = useEditClientAllocationContext();
  const crisisAllocations = allocations.filter(a => a.isCrisis);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useStoreActions(actions => actions.snackbar);
  const { onEditCrisisAllocation, onDeleteCrisisAllocation } = useStoreActions(
    actions => actions.crisis,
  );

  const submit = useCallback(
    async (values: IEditAllocationFormValues): Promise<boolean> => {
      const { changedCrisisAllocationsIds, deletedCrisisAllocationsIds } = values;

      if (!deletedCrisisAllocationsIds.length && !changedCrisisAllocationsIds.length) {
        return false;
      }

      setIsSubmitting(true);

      try {
        if (deletedCrisisAllocationsIds.length) {
          await Promise.all(
            deletedCrisisAllocationsIds.map(allocationId =>
              onDeleteCrisisAllocation({ clinicId, id: allocationId }),
            ),
          );
        }

        if (changedCrisisAllocationsIds.length) {
          await Promise.all(
            changedCrisisAllocationsIds.flatMap(updatedAllocationId => {
              const updatedAllocation = crisisAllocations.find(a => a.id === updatedAllocationId);

              if (!updatedAllocation) return [];

              return updatedAllocation.user.clients.map(client =>
                onEditCrisisAllocation({
                  clinicId,
                  id: String(updatedAllocation.id),
                  memo: getMemoByClientAndAllocation(
                    values.teamMembersAllocations,
                    client.id,
                    updatedAllocation.id,
                    updatedAllocation.user.id,
                  ),
                }),
              );
            }),
          );
        }

        return true;
      } catch (error) {
        showError(extractErrorMessage(error));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clinicId, crisisAllocations, onDeleteCrisisAllocation, onEditCrisisAllocation, showError],
  );

  return { submit, isSubmitting };
};

export default useEditCrisisAllocationSubmit;
